import { Modal } from 'shared/ui/modal/modal';
import { ExportModalProps, Report, Settings } from './export-modal.types';
import cls from './export-modal.module.scss';
import { LogoImport } from './ui/logo-import/logo-import';
import { ExportSettings } from './ui/export-settings/export-settings';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import Button from 'shared/ui/button/button';
import { formatDate } from 'shared/helpers/format-date';
import { useAppSelector } from 'store/hooks';
import { ReportPreview } from './ui/report-preview/report-preview';
import { ReportPreviewHandle } from './ui/report-preview/report-preview.types';
import { useEffect, useRef, useState } from 'react';
import {
  useGetExportConfigQuery,
  useSaveExportConfigMutation,
} from 'store/api/pdfReport/pdfReportApiSlice';
import {
  DEFAULT_SETTINGS,
  itemsFromSettings,
  settingsFromItems,
} from './export-modal.helpers';
import { InputField } from 'shared/ui/inputField/inputField';

export const ExportModal: React.FC<ExportModalProps> = (
  props: ExportModalProps
) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const { lastReport, selectedProject, domainId, reportGenerating } =
    useCachedSelectedProject();

  const activeReport = useAppSelector(state => state.history.activeReport);
  const historyMode = useAppSelector(state => state.history.historyMode);
  const modalReportDate = activeReport?.date ?? lastReport?.date;
  const [report, setReport] = useState<Report>({ settings: DEFAULT_SETTINGS });
  const [brandingName, setBrandingName] = useState('');
  const previewRef = useRef<ReportPreviewHandle>(null);
  const settingsInitialized = useRef(false);

  const { data: config, isLoading } = useGetExportConfigQuery(domainId ?? '', {
    skip: !domainId,
  });
  const [saveExportConfig] = useSaveExportConfigMutation();

  useEffect(() => {
    const logoUrl = config?.logo
      ? `data:${config.logo.mediaType};base64,${config.logo.mediaData}`
      : undefined;

    if (!settingsInitialized.current) {
      setReport({
        settings: settingsFromItems(config?.items),
        logoUrl,
        name: config?.name,
      });
      setBrandingName(config?.name ?? '');
      if (config) settingsInitialized.current = true;
    } else {
      setReport(prev => ({ ...prev, logoUrl }));
    }
  }, [config]);

  const handleSettingsChange = (settings: Settings) => {
    setReport(prev => ({ ...prev, settings }));
  };

  const persistConfig = () => {
    if (!domainId) return;
    return saveExportConfig({
      domainId,
      logo: config?.logo ?? null,
      name: brandingName,
      items: itemsFromSettings(report.settings, config?.items),
    });
  };

  const handleDownload = async () => {
    try {
      await persistConfig()?.unwrap();
    } catch {
      // swallow — printing should still proceed if the save fails
    }
    previewRef.current?.print();
  };

  const handleClose = () => {
    persistConfig();
    onClose();
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBrandingName(event.target.value);
    setReport(prev => ({ ...prev, name: event.target.value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={cls.exportModal}
      closeButton
    >
      <div className={cls.content}>
        <section className={cls.aside}>
          <section className={cls.heading}>
            <h2>{t('exportReport')}</h2>
            <p>
              {selectedProject?.domain} ·{' '}
              {formatDate(new Date(modalReportDate ?? ''))}
            </p>
          </section>
          <section className={cls.branding}>
            <LogoImport />
            <InputField
              className={cls.nameInput}
              placeholder={t('xoviAI')}
              label={t('name')}
              value={brandingName}
              onChange={handleOnChange}
            />
          </section>
          <ExportSettings
            settings={report.settings}
            onChange={handleSettingsChange}
          />
          <div className={cls.footer}>
            <Button
              theme='filled'
              tone='default'
              size='m'
              onClick={handleDownload}
              disabled={isLoading || (reportGenerating && !historyMode)}
            >
              {t('downloadPdf')}
            </Button>
          </div>
        </section>
        <section className={cls.info}>
          <div className={cls.previewCard}>
            <ReportPreview ref={previewRef} report={report} />
          </div>
        </section>
      </div>
    </Modal>
  );
};
